/**
 * The coding-model picker's contract with GET /autocoder/:customer/:job/models.
 *
 * Mirrors AutoCoderModelOption / AutoCoderModelsResponse in
 * goapi/pkg/server/autocoder_models.go. Hand-written rather than generated,
 * like types/llmLanes.ts, because these are response shapes rather than
 * domain structs.
 */

export interface CodingModelOption {
  /**
   * The canonical llm.ModelID — which IS the queue lane id. New selections
   * store this.
   */
  id: string
  label: string
  provider: string
  /**
   * Legacy strings older pipelines stored in `model_version` ("4", "mini").
   * The picker matches a saved value against id OR aliases so a legacy
   * pipeline still shows its real selection instead of an empty box.
   */
  aliases: string[]

  available: boolean
  unavailable_reason?: string

  tools: boolean
  reasoning: boolean
}

export interface CodingModelsResponse {
  models: CodingModelOption[]
  /** What this job codes with when a pipeline has not chosen (the cascade). */
  default: string
}

/**
 * Find the option a stored `model_version` refers to, by canonical id first and
 * legacy alias second. Returns undefined for a value no catalog entry claims —
 * which the picker must show as-is rather than silently swapping for a default,
 * because a pipeline coding against an unknown model is a fact worth seeing.
 */
export const findCodingModel = (
  models: CodingModelOption[],
  stored: string,
): CodingModelOption | undefined => {
  if (!stored) return undefined
  const key = stored.trim().toLowerCase()
  return models.find(
    m => m.id.toLowerCase() === key || m.aliases.some(a => a.toLowerCase() === key),
  )
}
